import { jsPDF } from 'jspdf';
import { Assessment } from "src/app/calvin-emr-shared/models/assessment.model";
import { CardiovascularAssessmentHelpers } from 'src/app/calvin-emr-shared/models/assessments/cardiovascular.model';
import { GastrointestinalAssessmentHelpers } from 'src/app/calvin-emr-shared/models/assessments/gastrointestinal.model';
import { GeneralAppearanceAssessmentHelpers } from 'src/app/calvin-emr-shared/models/assessments/general-appearance.model';
import { GenitourinaryAssessmentHelpers } from 'src/app/calvin-emr-shared/models/assessments/genitourinary.model';
import { HEENTAssessmentHelpers } from 'src/app/calvin-emr-shared/models/assessments/HEENT.model';
import { IntakeOutputAssessmentHelpers } from 'src/app/calvin-emr-shared/models/assessments/intake-output.model';
import { IntegumentaryAssessmentHelpers } from 'src/app/calvin-emr-shared/models/assessments/integumentary.model';
import { MusculoskeletalAssessmentHelpers } from 'src/app/calvin-emr-shared/models/assessments/musculoskeletal.model';
import { NeurologicalAssessmentHelpers } from 'src/app/calvin-emr-shared/models/assessments/neurological.model';
import { PainAssessmentHelpers } from 'src/app/calvin-emr-shared/models/assessments/pain.model';
import { ProviderNotifiedAssessmentHelpers } from 'src/app/calvin-emr-shared/models/assessments/provider-notified.model';
import { PsychosocialAssessmentHelpers } from 'src/app/calvin-emr-shared/models/assessments/psychosocial.model';
import { RespiratoryAssessmentHelpers } from 'src/app/calvin-emr-shared/models/assessments/respiratory.model';
import { Patient } from "src/app/calvin-emr-shared/models/patient.model";

export class AssessmentPdfGenerator {

  pdf: jsPDF;
  pageWidth: number;
  pageHeight: number;
  margin: number = 40;
  lineHeight = 18;
  leftColX = this.margin;
  rightColX: number;
  newSection = 20

  currY: number = this.margin;

  constructor(assessment: Assessment, patient: Patient) {
    // Initialize pdf download
    this.pdf = new jsPDF({
      unit: "pt",
      format: "a4"
    });

    // Set Page Width(595.28 pt)
    this.pageWidth = this.pdf.internal.pageSize.width;
    // Set Page Height(841.89 pt)
    this.pageHeight = this.pdf.internal.pageSize.height;


    this.rightColX = this.pageWidth / 2;
    this.currY += 30;

    this.pdf.setFontSize(18);
    this.pdf.setFont("Helvetica", "bold");
    this.pdf.text("EMR Assessment History Form", this.pageWidth / 2, this.currY, { align: "center" });

    const type = patient.getType();

    // Patient Information
    this.addSectionTitle("Patient Information");
    this.addTwoColumnText("Name", patient.getNameFirstMiddleLast(), "Date of Birth", patient.getDOBString());
    this.addTwoColumnText("Age", patient.getAge().toString(), "Sex", patient.getSex());
    this.addTwoColumnText("Height", patient.getHeightString(), "Weight", patient.getWeight().toString());
    this.addTwoColumnText("BMI", patient.getBMI().toString(), "Allergies", patient.getAllergies());
    this.addTwoColumnText("Assessment Date", "", "Current Active Medical Problems", patient.getCurrentMeds());
    this.addTwoColumnText("Code Status", patient.getCodeStatus(), "Isolation", patient.getIsolation());
    this.addTwoColumnText("Interdisciplinary Team Members", "", "Room Number", patient.getRoomNumber());
    this.addTwoColumnText("Unit", patient.getType(), "", "");
    this.currY += this.newSection;

    GeneralAppearanceAssessmentHelpers.toPDF(this, assessment.generalAppearance, patient);
    this.currY += this.newSection;

    HEENTAssessmentHelpers.toPDF(this, assessment.heent, patient);
    this.currY += this.newSection;

    CardiovascularAssessmentHelpers.toPDF(this, assessment.cardiovascular);
    this.currY += this.newSection;

    RespiratoryAssessmentHelpers.toPDF(this, assessment.respiratory, patient);
    this.currY += this.newSection;

    NeurologicalAssessmentHelpers.toPDF(this, assessment.neurological, patient);
    this.currY += this.newSection;

    MusculoskeletalAssessmentHelpers.toPDF(this, assessment.musculoskeletal, patient);
    this.currY += this.newSection;

    GastrointestinalAssessmentHelpers.toPDF(this, assessment.gastrointestinal, patient);
    this.currY += this.newSection;

    GenitourinaryAssessmentHelpers.toPDF(this, assessment.genitourinary, patient);
    this.currY += this.newSection;

    PsychosocialAssessmentHelpers.toPDF(this, assessment.psychosocial);
    IntegumentaryAssessmentHelpers.toPDF(this, assessment.integumentary);
    PainAssessmentHelpers.toPDF(this, assessment.painAssessment, patient);
    IntakeOutputAssessmentHelpers.toPDF(this, assessment.intakeOutput, patient);
    ProviderNotifiedAssessmentHelpers.toPDF(this, assessment.providerNotified);
  }

  checkAndAddPage = (pdf: jsPDF, requiredHeight: number) => {
    const bottomMargin = 20;

    if (this.currY + requiredHeight > this.pageHeight - bottomMargin) {
      pdf.addPage();
      this.currY = 40;
    }
  };

  addSectionTitle = (title: string) => {
    const sectionHeight = 20;
    this.checkAndAddPage(this.pdf, sectionHeight);

    this.pdf.setFont("Helvetica", "bold");
    this.pdf.setFontSize(12);
    this.pdf.text(title, this.leftColX, this.currY);
    this.currY += 10;

    this.pdf.setDrawColor(0);
    this.pdf.setLineWidth(0.5);
    this.pdf.line(this.leftColX, this.currY, this.pdf.internal.pageSize.width - this.leftColX, this.currY);
    this.currY += 20;
  };

  addOneColumnText = (title: string, text: string) => {
    const titleWidth = this.pdf.getTextWidth(`${title}:`) + 17;
    const maxTextWidth = this.pageWidth - this.leftColX - titleWidth - 10;

    // Wrap text based on available width
    const wrappedText = this.pdf.splitTextToSize(text || "N/A", maxTextWidth);

    // Calculate required height before printing
    const requiredHeight = wrappedText.length * this.lineHeight;

    // Check if a new page is needed
    this.checkAndAddPage(this.pdf, requiredHeight);

    this.pdf.setFont("Helvetica", "bold");
    this.pdf.text(`${title}:`, this.leftColX, this.currY);

    // Print wrapped text, ensuring the first line starts after the title
    this.pdf.setFont("Helvetica", "normal");
    this.pdf.text(wrappedText[0], this.leftColX + titleWidth, this.currY);

    for (let i = 1; i < wrappedText.length; i++) {
      this.currY += this.lineHeight;
      this.pdf.text(wrappedText[i], this.leftColX, this.currY);
    }

    this.currY += this.lineHeight;
  };


  addTwoColumnText = (label1: string, value1: string, label2: string, value2: string) => {
    const maxColumnWidth = (this.pdf.internal.pageSize.width / 2) - this.leftColX - 10;

    this.pdf.setFont("Helvetica", "bold");

    const label1Width = this.pdf.getTextWidth(`${label1}:`) + 5;
    const availableInlineWidth = maxColumnWidth - label1Width;

    const wrappedValue1 = this.pdf.splitTextToSize(value1 || "N/A", availableInlineWidth);
    const wrappedValue2 = this.pdf.splitTextToSize(value2 || "N/A", availableInlineWidth);

    const requiredHeight = Math.max(wrappedValue1.length, wrappedValue2.length) * this.lineHeight;

    this.checkAndAddPage(this.pdf, requiredHeight);

    this.pdf.text(`${label1}:`, this.leftColX, this.currY);
    this.pdf.setFont("Helvetica", "normal");
    this.pdf.text(wrappedValue1[0], this.leftColX + label1Width, this.currY);

    for (let i = 1; i < wrappedValue1.length; i++) {
      this.currY += this.lineHeight;
      this.pdf.text(wrappedValue1[i], this.leftColX, this.currY);
    }

    if (label2) {
      this.pdf.setFont("Helvetica", "bold");
      this.pdf.text(`${label2}:`, this.rightColX, this.currY - ((wrappedValue1.length - 1) * this.lineHeight));
      this.pdf.setFont("Helvetica", "normal");

      this.pdf.text(wrappedValue2[0], this.rightColX + this.pdf.getTextWidth(`${label2}:`) + 10, this.currY - ((wrappedValue1.length - 1) * this.lineHeight));

      for (let i = 1; i < wrappedValue2.length; i++) {
        this.currY += this.lineHeight;
        this.pdf.text(wrappedValue2[i], this.rightColX, this.currY);
      }
    }

    this.currY += this.lineHeight;
  };

  getResult() {
    return this.pdf;
  }

}